import { generateUID } from "verdi-common/utils/HelperFunctions";
import { BufferedAction } from "./bufferedAction.js";
/**
 *
 * @type {{event: EventHandler, hook : function}}
 */
let EventHooksPerUID = {};
export class EventHandler {
    static disposeOfAllHooksForUID(uid) {
        if (EventHooksPerUID[uid]) {
            [...EventHooksPerUID[uid]].forEach((eh) => {
                eh.event.removeListener(eh.hook);
            });
            delete EventHooksPerUID[uid];
        }
    }
    constructor(defaultArgs, buffer = 0) {
        this.defaultArgs = defaultArgs;
        this.bufferedTrigger = new BufferedAction(
            async () => {
                let resoloutionFunctions = [...this.resoloutionFunctions];
                let eventArgs = [...this.pendingEventArgs];
                this.pendingEventArgs = [];
                this.resoloutionFunctions = [];
                eventArgs.reverse();
                let awaiting = [];
                this.hooks.forEach((hook) => {
                    let fn = async (...eventArgs) => {
                        await hook(...eventArgs);
                    };
                    awaiting.push(
                        fn(...eventArgs).catch((e) => {
                            console.warn(`The following error occured in an event handler:`);
                            console.warn(e);
                        }),
                    );
                });
                for (let i = 0; i < awaiting.length; i++) {
                    await awaiting[i];
                }
                resoloutionFunctions.forEach((r) => {
                    r();
                });
            },
            buffer || 100,
            true,
            false,
        );
    }
    defaultArgs = {};
    /**
     *
     * @type {Set<function>}
     */
    hooks = new Set();
    HooksById = {};

    /**
     * Adds a listener that will be called when a device is loaded
     * hook will be called with event args format:
     * e = {
     *     target : {Device}
     * }
     * @param {function} hook
     * @param {string} uid UID to be able to reference the hook and remove it.
     * @return {string}
     */
    addListener(hook, uid = undefined) {
        this.hooks.add(hook);
        if (uid) {
            if (!EventHooksPerUID[uid]) {
                EventHooksPerUID[uid] = [];
            }
            EventHooksPerUID[uid].push({ event: this, hook: hook });
        }
        uid = uid || generateUID("EventHookID");
        this.HooksById[uid] = hook;
        return uid;
    }

    /**
     * Removes a listener so that the function will no longer be called when a device is loaded.
     * @param {function | string} hook or hook ID
     * @return {boolean} return true if operation was successful
     */
    removeListener(hook) {
        if (hook instanceof String || typeof hook === "string") {
            if (this.HooksById[hook] && this.hooks.has(this.HooksById[hook])) {
                if (EventHooksPerUID[hook]) {
                    for (let i = 0; i < EventHooksPerUID[hook].length; i++) {
                        if (
                            EventHooksPerUID[hook][i].event === this &&
                            EventHooksPerUID[hook][i].hook === this.HooksById[hook]
                        ) {
                            EventHooksPerUID[hook].splice(i, 1);
                            i--;
                        }
                    }
                }
                this.hooks.delete(this.HooksById[hook]);
                delete this.HooksById[hook];
                return true;
            }
        }
        if (this.hooks.has(hook)) {
            this.hooks.delete(hook);
            Object.keys(this.HooksById).forEach((id) => {
                if (!this.hooks.has(this.HooksById[id])) {
                    delete this.HooksById[id];
                }
            });
            Object.keys(EventHooksPerUID).forEach((id) => {
                for (let i = 0; i < EventHooksPerUID[id].length; i++) {
                    if (EventHooksPerUID[id][i].event === this && EventHooksPerUID[id][i].hook === hook) {
                        EventHooksPerUID[id].splice(i, 1);
                        i--;
                    }
                }
            });
            return true;
        } else {
            return false;
        }
    }

    /**
     * Removes a listener so that the function will no longer be called when a device is loaded.
     * @param {function | string} listeners a list of hooks, listeners, and/or hoook ids
     * @return {boolean} return true if all listeners were successfully found and removed;
     */
    removeListeners(...listeners) {
        let successful = true;
        listeners.forEach((hook) => {
            if (this.HooksById[hook]) {
                if (this.hooks.has(this.HooksById[hook])) {
                    this.hooks.delete(this.HooksById[hook]);
                    delete this.HooksById[hook];
                    return true;
                }
            }
            if (this.hooks.has(hook)) {
                this.hooks.delete(hook);
                return true;
            }
            successful = false;
            return false;
        });
        if (Object.keys(this.HooksById).length > this.hooks.size) {
            Object.keys(this.HooksById).forEach((id) => {
                if (!this.hooks.has(this.HooksById[id])) {
                    delete this.HooksById[id];
                }
            });
        }
        return successful;
    }

    /**
     * Clears all listeners
     */
    clearListeners() {
        this.hooks = new Set();
        this.HooksById = {};
        Object.keys(EventHooksPerUID).forEach((uid) => {
            for (let i = 0; i < EventHooksPerUID[uid].length; i++) {
                if (EventHooksPerUID[uid][i].event === this) {
                    EventHooksPerUID[uid].splice(i, 1);
                    i--;
                }
            }
        });
    }

    fingerprint = 0;
    buffer = 0;
    pendingEventArgs = [];
    resoloutionFunctions = [];
    /**
     * Triggers all hooks to be called with arguments e
     * @param e
     * @return Promise<void>
     */
    trigger(e) {
        if (this.buffer === 0) {
            let temp = async () => {
                let awaiting = [];
                this.hooks.forEach((hook) => {
                    let fn = async (...eventArgs) => {
                        await hook(...eventArgs);
                    };
                    awaiting.push(
                        fn(e).catch((e) => {
                            console.warn(`The following error occured in an event handler:`);
                            console.warn(e);
                        }),
                    );
                });
                for (let i = 0; i < awaiting.length; i++) {
                    await awaiting[i];
                }
            };
            return temp();
        }
        this.fingerprint++;
        e = Object.assign(e ? e : {}, this.defaultArgs ? this.defaultArgs : {});
        let newE = {};
        newE = Object.assign(newE, e);
        this.pendingEventArgs.push(newE);
        let promise = new Promise((resolve) => {
            this.resoloutionFunctions.push(resolve);
        });

        this.bufferedTrigger.trigger();

        return promise;
    }
}
